import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../config/api';
import ConfirmModal from './common/ConfirmModal';
import './ReviewsManager.css';

const ReviewsManager = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all', 'approved', 'pending'
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
    limit: 20
  });
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: '',
    message: '',
    onConfirm: null,
    type: 'danger'
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchReviews();
  }, [filter, pagination.page]);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
      });

      if (filter === 'approved') {
        queryParams.append('isApproved', 'true');
      } else if (filter === 'pending') {
        queryParams.append('isApproved', 'false');
      }

      const response = await axios.get(`${API_URL}/api/reviews?${queryParams}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setReviews(response.data.data);
      setPagination(response.data.pagination);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (reviewId) => {
    try {
      await axios.put(
        `${API_URL}/api/reviews/${reviewId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Review approved successfully');
      fetchReviews();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to approve review');
    }
  };

  const handleReject = async (reviewId) => {
    try {
      await axios.put(
        `${API_URL}/api/reviews/${reviewId}/reject`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Review rejected successfully');
      fetchReviews();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reject review');
    }
  };

  const handleDelete = (reviewId) => {
    setConfirmModal({
      show: true,
      title: 'Delete Review',
      message: 'Are you sure you want to delete this review? This action cannot be undone.',
      type: 'danger',
      onConfirm: async () => {
        try {
          await axios.delete(`${API_URL}/api/reviews/${reviewId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          toast.success('Review deleted successfully');
          setConfirmModal({ show: false, title: '', message: '', onConfirm: null, type: 'danger' });
          fetchReviews();
        } catch (error) {
          toast.error(error.response?.data?.message || 'Failed to delete review');
          setConfirmModal({ show: false, title: '', message: '', onConfirm: null, type: 'danger' });
        }
      }
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderStars = (rating) => {
    return (
      <div className="stars">
        {[1, 2, 3, 4, 5].map((star) => (
          <span key={star} className={star <= rating ? 'star filled' : 'star'}>
            ★
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="reviews-manager">
      <div className="reviews-header">
        <h1>Reviews Manager</h1>
        <div className="filter-buttons">
          <button
            className={filter === 'all' ? 'active' : ''}
            onClick={() => {
              setFilter('all');
              setPagination({ ...pagination, page: 1 });
            }}
          >
            All Reviews
          </button>
          <button
            className={filter === 'approved' ? 'active' : ''}
            onClick={() => {
              setFilter('approved');
              setPagination({ ...pagination, page: 1 });
            }}
          >
            Approved
          </button>
          <button
            className={filter === 'pending' ? 'active' : ''}
            onClick={() => {
              setFilter('pending');
              setPagination({ ...pagination, page: 1 });
            }}
          >
            Pending
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading reviews...</div>
      ) : reviews.length === 0 ? (
        <div className="no-reviews">
          <p>No reviews found.</p>
        </div>
      ) : (
        <>
          <div className="reviews-stats">
            <p>
              Showing {reviews.length} of {pagination.total} reviews
            </p>
          </div>

          <div className="reviews-table-container">
            <table className="reviews-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>User</th>
                  <th>Email</th>
                  <th>Rating</th>
                  <th>Comment</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {reviews.map((review) => (
                  <tr key={review._id}>
                    <td>
                      <div className="product-name">
                        {review.product?.name || 'Unknown Product'}
                      </div>
                    </td>
                    <td>{review.userName}</td>
                    <td>{review.userEmail}</td>
                    <td>
                      <div className="rating-cell">
                        {renderStars(review.rating)}
                        <span className="rating-value">({review.rating})</span>
                      </div>
                    </td>
                    <td>
                      <div className="comment-cell">
                        {review.comment.length > 100
                          ? `${review.comment.substring(0, 100)}...`
                          : review.comment}
                      </div>
                    </td>
                    <td>
                      <span
                        className={`status-badge ${
                          review.isApproved ? 'approved' : 'pending'
                        }`}
                      >
                        {review.isApproved ? '✓ Approved' : '⏳ Pending'}
                      </span>
                    </td>
                    <td>{formatDate(review.createdAt)}</td>
                    <td>
                      <div className="action-buttons">
                        {!review.isApproved && (
                          <button
                            className="btn-approve"
                            onClick={() => handleApprove(review._id)}
                            title="Approve Review"
                          >
                            ✓ Approve
                          </button>
                        )}
                        {review.isApproved && (
                          <button
                            className="btn-reject"
                            onClick={() => handleReject(review._id)}
                            title="Reject Review"
                          >
                            ✗ Reject
                          </button>
                        )}
                        <button
                          className="btn-delete"
                          onClick={() => handleDelete(review._id)}
                          title="Delete Review"
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() =>
                  setPagination({ ...pagination, page: pagination.page - 1 })
                }
                disabled={pagination.page === 1}
                className="pagination-btn"
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                onClick={() =>
                  setPagination({ ...pagination, page: pagination.page + 1 })
                }
                disabled={pagination.page === pagination.totalPages}
                className="pagination-btn"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ReviewsManager;

